import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { Icon } from "@iconify/react";

export default function AddSubSection({ open, onClose, category, eventId, subId }) {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const [imagePreview, setImagePreview] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const modalRef = useRef(null);
    const canvasRef = useRef(null);
    const [mapImage, setMapImage] = useState(null);
    const [imageObj, setImageObj] = useState(null);

    // Drawing State
    const [sections, setSections] = useState([]);
    const [drawing, setDrawing] = useState(false);
    const [startPos, setStartPos] = useState(null);
    const [tempRect, setTempRect] = useState(null);

    const [selectedSectionId, setSelectedSectionId] = useState(null);
    const [dragging, setDragging] = useState(false);
    const [resizing, setResizing] = useState(null); // "nw" | "ne" | "sw" | "se"
    const [dragOffset, setDragOffset] = useState(null);
    const [editingSection, setEditingSection] = useState(null);

    const resetStates = () => {
        setEditingSection(null);
        setDrawing(false);
        setDragging(false);
        setResizing(null);
        setTempRect(null);
        setStartPos(null);
        setSelectedSectionId(null);
        setDragOffset(null);
    };

    useEffect(() => {
        if (mapImage) {
            const img = new Image();
            img.src = URL.createObjectURL(mapImage);
            img.onload = () => setImageObj(img);
        }
    }, [mapImage]);

    // Draw image + sections + temp rectangle
    useEffect(() => {
        if (!imageObj) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        canvas.width = imageObj.width;
        canvas.height = imageObj.height;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(imageObj, 0, 0);

        // Draw saved sections

        sections.forEach(s => {
            ctx.strokeStyle = s.color;
            ctx.lineWidth = 2;
            ctx.strokeRect(s.coordinates.x, s.coordinates.y, s.coordinates.width, s.coordinates.height);

            ctx.fillStyle = s.color;
            ctx.font = "16px Arial";
            ctx.fillText(s.name, s.coordinates.x + 4, s.coordinates.y - 4);

            // If selected, draw handles
            if (s.id === selectedSectionId) {
                const { x, y, width, height } = s.coordinates;
                const handleSize = 8;
                const handles = [
                    { x, y, cursor: "nw-resize", pos: "nw" },
                    { x: x + width, y, cursor: "ne-resize", pos: "ne" },
                    { x, y: y + height, cursor: "sw-resize", pos: "sw" },
                    { x: x + width, y: y + height, cursor: "se-resize", pos: "se" }
                ];
                ctx.fillStyle = "#fff";
                ctx.strokeStyle = "#000";
                handles.forEach(h => {
                    ctx.fillRect(h.x - handleSize / 2, h.y - handleSize / 2, handleSize, handleSize);
                    ctx.strokeRect(h.x - handleSize / 2, h.y - handleSize / 2, handleSize, handleSize);
                });
            }
        });

        // Draw temp rectangle
        if (tempRect) {
            ctx.strokeStyle = "red";
            ctx.setLineDash([6, 4]);
            ctx.strokeRect(tempRect.x, tempRect.y, tempRect.width, tempRect.height);
            ctx.setLineDash([]);
        }
    }, [imageObj, sections, tempRect]);

    const getMousePos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        // scale factor between CSS size and actual canvas resolution
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY,
        };
    };

    // Mouse Handlers
    const handleMouseDown = (e) => {
        const pos = getMousePos(e);

        // Check if clicking inside a section
        for (let s of sections) {
            const { x, y, width, height } = s.coordinates;

            // check handle hit
            const handleSize = 8;
            const handles = {
                nw: { x, y },
                ne: { x: x + width, y },
                sw: { x, y: y + height },
                se: { x: x + width, y: y + height }
            };

            for (let key in handles) {
                const h = handles[key];
                if (
                    pos.x >= h.x - handleSize &&
                    pos.x <= h.x + handleSize &&
                    pos.y >= h.y - handleSize &&
                    pos.y <= h.y + handleSize
                ) {
                    setSelectedSectionId(s.id);
                    setResizing(key);
                    setDragging(false);
                    return;
                }
            }

            // check inside rectangle
            if (
                pos.x >= x && pos.x <= x + width &&
                pos.y >= y && pos.y <= y + height
            ) {
                setSelectedSectionId(s.id);
                setDragging(true);
                setDragOffset({ x: pos.x - x, y: pos.y - y });
                return;
            }
        }

        // Otherwise → start drawing a new one
        setSelectedSectionId(null);
        setStartPos(pos);
        setDrawing(true);
    };

    const handleMouseMove = (e) => {
        const pos = getMousePos(e);

        if (resizing && selectedSectionId) {
            setSections(prev =>
                prev.map(s => {
                    if (s.id !== selectedSectionId) return s;
                    let { x, y, width, height } = s.coordinates;

                    switch (resizing) {
                        case "nw":
                            width += x - pos.x;
                            height += y - pos.y;
                            x = pos.x;
                            y = pos.y;
                            break;
                        case "ne":
                            width = pos.x - x;
                            height += y - pos.y;
                            y = pos.y;
                            break;
                        case "sw":
                            width += x - pos.x;
                            x = pos.x;
                            height = pos.y - y;
                            break;
                        case "se":
                            width = pos.x - x;
                            height = pos.y - y;
                            break;
                        default:
                            break;
                    }
                    return { ...s, coordinates: { x, y, width, height } };
                })
            );
            return;
        }

        if (dragging && selectedSectionId) {
            setSections(prev =>
                prev.map(s => {
                    if (s.id !== selectedSectionId) return s;
                    return {
                        ...s,
                        coordinates: {
                            ...s.coordinates,
                            x: pos.x - dragOffset.x,
                            y: pos.y - dragOffset.y,
                        },
                    };
                })
            );
            return;
        }

        if (drawing && startPos) {
            setTempRect({
                x: startPos.x,
                y: startPos.y,
                width: pos.x - startPos.x,
                height: pos.y - startPos.y,
            });
        }
    };

    const handleMouseUp = () => {
        if (resizing) {
            setResizing(null);
        }
        if (dragging) {
            setDragging(false);
        }
        if (drawing) {
            if (tempRect && Math.abs(tempRect.width) > 5 && Math.abs(tempRect.height) > 5) {
                const fixedCoords = {
                    x: tempRect.width < 0 ? tempRect.x + tempRect.width : tempRect.x,
                    y: tempRect.height < 0 ? tempRect.y + tempRect.height : tempRect.y,
                    width: Math.abs(tempRect.width),
                    height: Math.abs(tempRect.height),
                };

                setEditingSection({
                    ...fixedCoords,
                    tempId: Date.now(),
                });
            }
            setTempRect(null);
            setDrawing(false);
        }
    };

    // Build categories from sections
    const categories = [];
    sections.forEach(s => {
        if (!categories.some(c => c.id === s.category)) {
            categories.push({ id: s.category, name: s.category, color: s.color });
        }
    });

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };
        if (open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open, onClose]);

    const handleImagePreview = (e, setFieldValue, fieldName) => {
        const file = e.currentTarget.files[0];
        setFieldValue(fieldName, file);
        setImagePreview(file ? URL.createObjectURL(file) : null);
    };

    const fetchAppMenu = async () => {
        if (!category) return;
        const endpointMap = {
            schedule: `event-service/app-schedule-category/${subId}`,
            speakers: `event-service/app-event-speaker/${subId}`,
            announcement: `event-service/app-announcement/${subId}`,
            sponsors: `event-service/app-sponsor-category/${subId}`,
            map: `event-service/app-venue-map/${subId}`,
            exhibitors: `event-service/app-exhibitor-category/${subId}`,
        };

        const key = category.trim().toLowerCase();
        if (!endpointMap[key]) {
            console.warn(`No API endpoint found for category: ${category}`);
            return;
        }

        try {
            const response = await axios.get(`${apiUrl}${endpointMap[key]}`, {
                headers: { Authorization: `Bearer ${bearerToken}` },
            });

            if (!response.data?.success || !response.data?.data) {
                throw new Error(`Failed to fetch ${category}`);
            }

            const data = response.data.data;

            // Map API data → Formik's values
            switch (key) {
                case "schedule":
                    formik.setValues({ name: data.name || "", icon: "" });
                    break;
                case "speakers":
                    formik.setValues({
                        name: data.name || "",
                        designation: data.designation || "",
                        description: data.description || "",
                        company: data.company || "",
                        profilePic: "",
                    });
                    setImagePreview(data.profilePicUrl || null);
                    break;
                case "announcement":
                    formik.setValues({ name: data.name || "" });
                    break;
                case "sponsors":
                    formik.setValues({ name: data.name || "" });
                    break;
                case "exhibitors":
                    formik.setValues({ name: data.name || "" });
                    break;
                case "map":
                    formik.setValues({
                        name: data.name || "",
                        description: data.description || "",
                    });
                    break;
                default:
                    break;
            }

        } catch (error) {
            console.error(error.response?.data?.message || error.message);
        }
    };

    useEffect(() => {
        if (open && category && subId) {
            fetchAppMenu();
        }
    }, [category, subId, open]);

    const buildUrl = (baseUrl) => `${apiUrl}${baseUrl}${subId ? `/${subId}` : ""}`;

    const formConfigs = {
        schedule: {
            initialValues: { name: '', icon: '' },
            validationSchema: Yup.object({
                name: Yup.string().required('Required'),
            }),
            onSubmit: async (values) => {
                const params = new URLSearchParams();
                params.append("eventId", eventId);
                Object.entries(values).forEach(([k, v]) => params.append(k, v));

                await axios({
                    method: subId ? "put" : "post",
                    url: buildUrl('event-service/app-schedule-category'),
                    data: params,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                        "Content-Type": "application/x-www-form-urlencoded",
                        accept: "application/json",
                    },
                });
            },
            render: ({ values, errors, touched, submitCount, handleChange, handleBlur }) => (
                <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="name"
                        placeholder="Name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                    {(touched.name || submitCount > 0) && errors.name && <div className="text-red-500 text-xs">{errors.name}</div>}
                </>
            ),
        },

        speakers: {
            initialValues: { name: '', designation: '', description: '', company: '', profilePic: '' },
            validationSchema: Yup.object({
                name: Yup.string().required('Required'),
                designation: Yup.string().required('Required'),
                description: Yup.string().required('Required'),
                company: Yup.string().required('Required'),
                // profilePic: Yup.mixed()
                //     .test('fileSize', 'File size is too large', (value) => {
                //         if (typeof value === 'string') return true; // It's an existing image URL
                //         return value && value.size <= 1 * 1024 * 1024;
                //     })
                //     .test('fileType', 'Unsupported file format', (value) => {
                //         if (typeof value === 'string') return true; // Existing image URL
                //         return value && ['image/jpeg', 'image/png', 'image/gif'].includes(value.type);
                //     }),

            }),
            onSubmit: async (values) => {
                const formData = new FormData();
                formData.append("eventId", eventId);
                Object.entries(values).forEach(([k, v]) => formData.append(k, v));

                await axios({
                    method: subId ? "put" : "post",
                    url: buildUrl('event-service/app-event-speaker'),
                    data: formData,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                        accept: "application/json",
                    },
                });
            },
            render: ({ values, errors, touched, submitCount, handleChange, handleBlur, setFieldValue }) => (
                <>
                    {['name', 'designation', 'company'].map((field) => (
                        <div key={field} className="mb-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {field.charAt(0).toUpperCase() + field.slice(1)} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name={field}
                                placeholder={field}
                                value={values[field]}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                            {(touched[field] || submitCount > 0) && errors[field] && <div className="text-red-500 text-xs">{errors[field]}</div>}
                        </div>
                    ))}
                    <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            name="description"
                            value={values.description}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                        {(touched.description || submitCount > 0) && errors.description && (
                            <div className="text-red-500 text-xs">{errors.description}</div>
                        )}
                    </div>

                    <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center relative">
                            {imagePreview ? (
                                <>
                                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-lg" />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setImagePreview(null);
                                            setFieldValue('profilePic', null);
                                        }}
                                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center"
                                    >
                                        <Icon icon="material-symbols-light:close-rounded" />
                                    </button>
                                </>
                            ) : (
                                <svg
                                    className="w-6 h-6 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                            )}
                        </div>
                        <label className="cursor-pointer">
                            <span className="text-blue-500 border border-blue-500 rounded-md px-4 py-1">
                                {imagePreview ? 'Change Profile Pic' : 'Choose Profile Pic'}
                            </span>
                            <input
                                type="file"
                                name="profilePic"
                                className="hidden"
                                accept="image/*"
                                onChange={(e) => handleImagePreview(e, setFieldValue, 'profilePic')}
                            />
                        </label>
                    </div>
                    {(touched.profilePic || submitCount > 0) && errors.profilePic && <div className="text-red-500 text-xs">{errors.profilePic}</div>}
                </>
            ),
        },

        announcement: {
            initialValues: { name: '' },
            validationSchema: Yup.object({ name: Yup.string().required('Required') }),
            onSubmit: async (values) => {
                const params = new URLSearchParams();
                params.append("eventId", eventId);
                Object.entries(values).forEach(([k, v]) => params.append(k, v));

                await axios({
                    method: subId ? "put" : "post",
                    url: buildUrl('event-service/app-announcement'),
                    data: params,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                        "Content-Type": "application/x-www-form-urlencoded",
                        accept: "application/json",
                    },
                });
            },
            render: ({ values, errors, touched, submitCount, handleChange, handleBlur }) => (
                <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Announcement Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="name"
                        placeholder="Name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                    {(touched.name || submitCount > 0) && errors.name && <div className="text-red-500 text-xs">{errors.name}</div>}
                </>
            ),
        },

        sponsors: {
            initialValues: { name: '' },
            validationSchema: Yup.object({ name: Yup.string().required('Required') }),
            onSubmit: async (values) => {
                const params = new URLSearchParams();
                params.append("eventId", eventId);
                Object.entries(values).forEach(([k, v]) => params.append(k, v));

                await axios({
                    method: subId ? "put" : "post",
                    url: buildUrl('event-service/app-sponsor-category'),
                    data: params,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                        "Content-Type": "application/x-www-form-urlencoded",
                        accept: "application/json",
                    },
                });
            },
            render: ({ values, errors, touched, submitCount, handleChange, handleBlur }) => (
                <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="name"
                        placeholder="Name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                    {(touched.name || submitCount > 0) && errors.name && <div className="text-red-500 text-xs">{errors.name}</div>}
                </>
            ),
        },

        exhibitors: {
            initialValues: { name: '' },
            validationSchema: Yup.object({ name: Yup.string().required('Required') }),
            onSubmit: async (values) => {
                const params = new URLSearchParams();
                params.append("eventId", eventId);
                Object.entries(values).forEach(([k, v]) => params.append(k, v));

                await axios({
                    method: subId ? "put" : "post",
                    url: buildUrl('event-service/app-exhibitor-category'),
                    data: params,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                        "Content-Type": "application/x-www-form-urlencoded",
                        accept: "application/json",
                    },
                });
            }, render: ({ values, errors, touched, submitCount, handleChange, handleBlur }) => (
                <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="name"
                        placeholder="Name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                    {(touched.name || submitCount > 0) && errors.name && <div className="text-red-500 text-xs">{errors.name}</div>}
                </>
            ),
        },

        map: {
            initialValues: { name: '', description: '', mapImage: null },
            validationSchema: Yup.object({
                name: Yup.string().required('Required'),
                description: Yup.string().required('Required'),
                mapImage: Yup.mixed().when('subId', {
                    is: (subId), // if subId is empty / null / undefined
                    then: (schema) => schema.required('Map image is required'),
                    otherwise: (schema) => schema.notRequired(),
                }),
            }),

            onSubmit: async (values) => {
                if (!sections.length && !subId) {
                    alert("Please draw at least one section on the map.");
                    return;
                }

                const formData = new FormData();
                formData.append("eventId", eventId);
                formData.append("name", values.name);
                formData.append("description", values.description);
                formData.append("mapImage", values.mapImage);

                // Attach JSON under key "map"
                formData.append(
                    "map",
                    JSON.stringify({
                        config: {
                            zoom: { min: 0.5, max: 3, default: 1 },
                            position: { x: 0, y: 0 }
                        },
                        sections,
                        categories: sections.map(s => ({
                            id: s.category,
                            name: s.category,
                            color: s.color
                        }))
                    })
                );

                await axios({
                    method: subId ? "put" : "post",
                    url: buildUrl("event-service/app-venue-map"),
                    data: formData,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                        "Content-Type": "multipart/form-data",
                        accept: "application/json",
                    },
                });
            },
            render: ({ values, errors, touched, submitCount, handleChange, handleBlur, setFieldValue }) => (
                <>
                    <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Map Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={values.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                        {(touched.name || submitCount > 0) && errors.name && (
                            <div className="text-red-500 text-xs">{errors.name}</div>
                        )}
                    </div>

                    <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            name="description"
                            value={values.description}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                        {(touched.description || submitCount > 0) && errors.description && (
                            <div className="text-red-500 text-xs">{errors.description}</div>
                        )}
                    </div>

                    <label className="block text-sm font-medium text-gray-700 mb-1">Map Image</label>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                                setFieldValue("mapImage", file);
                                setMapImage(file); // triggers canvas rendering
                                setImagePreview(URL.createObjectURL(file));
                            }
                        }}
                    />
                    {(touched.mapImage || submitCount > 0) && errors.mapImage && (
                        <div className="text-red-500 text-xs">{errors.mapImage}</div>
                    )}

                    {imageObj && (
                        <div className='relative'>
                            <canvas
                                ref={canvasRef}
                                style={{ border: "1px solid #ccc", marginTop: "10px", cursor: "crosshair", maxWidth: "100%" }}
                                onMouseDown={handleMouseDown}
                                onMouseMove={handleMouseMove}
                                onMouseUp={handleMouseUp}
                            />
                            {editingSection && (
                                <div
                                    className="absolute bg-white shadow-md border rounded-md p-2 z-50"
                                    style={{
                                        top: editingSection.y + canvasRef.current?.offsetTop - 40 || 0,
                                        left: editingSection.x + canvasRef.current?.offsetLeft || 0,
                                    }}
                                >
                                    <input
                                        type="text"
                                        placeholder="Section name"
                                        className="border p-1 text-sm mr-2"
                                        value={editingSection.name || ""}
                                        onChange={(e) =>
                                            setEditingSection((prev) => ({ ...prev, name: e.target.value }))
                                        }
                                    />
                                    <input
                                        type="text"
                                        placeholder="Category Name"
                                        className="border p-1 text-sm mr-2"
                                        value={editingSection.category || ""}
                                        onChange={(e) =>
                                            setEditingSection((prev) => ({ ...prev, category: e.target.value }))
                                        }
                                    />
                                    <button
                                        className="bg-blue-500 text-white px-2 py-1 rounded text-sm mr-1"
                                        onClick={() => {
                                            if (!editingSection.name || !editingSection.category) {
                                                alert("Name and Category required");
                                                return;
                                            }
                                            const normalizeId = (str) =>
                                                str.trim().toLowerCase().replace(/\s+/g, "-");
                                            const id = normalizeId(editingSection.name);
                                            const category = normalizeId(editingSection.category);
                                            const color = "#4A90E2";

                                            setSections((prev) => [
                                                ...prev,
                                                {
                                                    id,
                                                    name: editingSection.name,
                                                    color,
                                                    coordinates: {
                                                        x: editingSection.x,
                                                        y: editingSection.y,
                                                        width: editingSection.width,
                                                        height: editingSection.height,
                                                    },
                                                    category,
                                                },
                                            ]);
                                            resetStates();
                                        }}
                                    >
                                        Save
                                    </button>
                                    {/* <button
                                        className="bg-gray-300 px-2 py-1 rounded text-sm"
                                        onClick={resetStates}
                                    >
                                        Cancel
                                    </button> */}
                                </div>
                            )}
                        </div>
                    )}
                </>
            ),
        },
    };

    const safeConfig = formConfigs[category?.trim()?.toLowerCase()] || {
        initialValues: {},
        validationSchema: Yup.object({}),
        onSubmit: () => { },
        render: () => null,
    };

    const formik = useFormik({
        initialValues: safeConfig.initialValues,
        validationSchema: safeConfig.validationSchema,
        validateOnBlur: true,
        validateOnChange: false,
        onSubmit: async (values, { resetForm }) => {
            setIsLoading(true);
            try {
                await safeConfig.onSubmit(values);
                resetForm();
                onClose();
                setImagePreview(null);
            } catch (err) {
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        },
    });

    if (!open) return null;

    const handleBackdropClick = () => {
        formik.resetForm();
        setImagePreview(null);
        onClose();
        resetStates();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            {/* Background overlay */}
            <div className="absolute inset-0 bg-black opacity-50" onClick={handleBackdropClick}></div>

            {/* Modal */}
            <div className="bg-white rounded-lg w-full max-w-xl z-50 relative">
                {/* Header */}
                <div className="bg-blue-500 text-white py-3 px-6 rounded-lg w-3/4 mx-auto relative z-10 -mt-6">
                    <h2 className="text-xl font-semibold text-center">{subId ? 'Edit' : 'Add New'} {category.trim().charAt(0).toUpperCase() + category.trim().slice(1).toLowerCase()}</h2>
                </div>

                <div className="relative bg-white p-6 space-y-6 shadow-md rounded-b-lg overflow-y-auto max-h-[70vh]">
                    <form onSubmit={formik.handleSubmit}>
                        {safeConfig.render({ ...formik, imagePreview, setImagePreview })}
                        <div className="flex justify-end gap-2">
                            <button onClick={handleBackdropClick} type="button" className="bg-gray-300 text-gray-700 mt-4 px-4 py-2 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500">
                                Cancel
                            </button>
                            <button type="submit" disabled={isLoading} className="bg-blue-500 text-white mt-4 px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500">
                                {subId
                                    ? (isLoading ? "Updating..." : "Update")
                                    : (isLoading ? "Saving..." : "Save")
                                }
                            </button>
                        </div>
                    </form>
                </div>
            </div>

        </div>
    );
}

AddSubSection.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    eventId: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    subId: PropTypes.string,
};